import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { DEFAULT_ROLES } from '@cls/shared';
import { BcSaveTemplateModal } from './BcSaveTemplateModal.js';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface Worker { id: string; name: string; role: string; status: string; phone: string; email: string | null; core: boolean; rtwStatus: string | null; rtwExpiry: string | null; projects?: string[]; }
interface Project { id: string; code: string; name: string; status: string; }

interface Props { onClose: () => void; seedFromFiltered?: boolean; }

export function BroadcastComposeModal({ onClose }: Props) {
  const qc = useQueryClient();
  const { values: roles } = useReferenceList('roles', DEFAULT_ROLES);
  const { values: statuses } = useReferenceList('statuses', ['available', 'deployed', 'unavailable']);
  const [fRole, setFRole] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fProject, setFProject] = useState('');
  const [fCore, setFCore] = useState(false);
  const [fRtw, setFRtw] = useState(false);
  const [chWa, setChWa] = useState(true);
  const [chSms, setChSms] = useState(false);
  const [chEmail, setChEmail] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });

  const audience = useMemo(() => {
    const selectedProject = projects.find((p) => p.id === fProject);
    return workers.filter((w) => {
      if (fRole && w.role !== fRole) return false;
      if (fStatus && w.status !== fStatus) return false;
      if (fCore && !w.core) return false;
      if (fRtw) {
        const hasIssue = !w.rtwStatus || w.rtwStatus === 'none' || (w.rtwStatus === 'visa' && w.rtwExpiry && new Date(w.rtwExpiry).getTime() < Date.now() + 90 * 86_400_000);
        if (!hasIssue) return false;
      }
      if (fProject && selectedProject) {
        const code = selectedProject.code;
        const onProj = (w.projects ?? []).includes(code) || (w.projects ?? []).includes(fProject);
        if (!onProj) return false;
      }
      return true;
    });
  }, [workers, projects, fRole, fStatus, fCore, fRtw, fProject]);

  const audienceLabel = audience.length === workers.length ? 'all workers' : `${audience.length} worker${audience.length !== 1 ? 's' : ''}`;

  const send = useMutation({
    mutationFn: async () => {
      const channels: string[] = [];
      if (chWa) channels.push('whatsapp');
      if (chSms) channels.push('sms');
      if (chEmail) channels.push('email');
      return api.post('/broadcasts', {
        messageBody: body,
        subject: chEmail ? subject : null,
        channels,
        recipientIds: audience.map((w) => w.id),
        audienceSummary: `${audience.length} workers${fRole ? ` · ${fRole}` : ''}${fStatus ? ` · ${fStatus}` : ''}${fCore ? ' · core' : ''}`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      toast.success('Broadcast sent', `${audience.length} recipient${audience.length !== 1 ? 's' : ''}`);
      onClose();
    },
    onError: (e: any) => toast.error('Send failed', e?.response?.data?.message ?? 'Check and try again'),
  });

  const copyNumbers = () => {
    const nums = audience.map((w) => w.phone).filter(Boolean).join(', ');
    navigator.clipboard.writeText(nums);
    toast.info('Copied', `${audience.length} numbers to clipboard`);
  };

  const insertPlaceholder = (p: string) => setBody((b) => b + p);

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 760, maxWidth: '98vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">📢 Broadcast Message</div>
        <div className="modal-sub">Send a message to a group of workers. Records retained in Broadcast History.</div>

        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div className="sec-title" style={{ margin: '0 0 8px', fontSize: 10, color: 'var(--accent)' }}>1. Audience</div>
          <div className="fg2">
            <div className="form-row">
              <label className="form-label" style={{ fontSize: 10 }}>Role</label>
              <select className="form-select" id="bc-filter-role" value={fRole} onChange={(e) => setFRole(e.target.value)}>
                <option value="">All roles</option>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label className="form-label" style={{ fontSize: 10 }}>Status</label>
              <select className="form-select" id="bc-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                <option value="">All statuses</option>
                {statuses.map((s) => <option key={s} value={s}>{`${s.charAt(0).toUpperCase() + s.slice(1)} only`}</option>)}
              </select>
            </div>
          </div>
          <div className="fg2">
            <div className="form-row">
              <label className="form-label" style={{ fontSize: 10 }}>Project</label>
              <select className="form-select" id="bc-filter-project" value={fProject} onChange={(e) => setFProject(e.target.value)}>
                <option value="">Any project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label className="form-label" style={{ fontSize: 10 }}>Additional</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}><input type="checkbox" id="bc-filter-core" checked={fCore} onChange={(e) => setFCore(e.target.checked)} style={{ accentColor: 'var(--accent)' }} /> Core only</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}><input type="checkbox" id="bc-filter-rtw-issues" checked={fRtw} onChange={(e) => setFRtw(e.target.checked)} style={{ accentColor: 'var(--accent)' }} /> RTW issues only</label>
              </div>
            </div>
          </div>
          <div id="bc-audience-preview" style={{ marginTop: 8, padding: '7px 10px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--muted)' }}>
            <strong style={{ color: 'var(--accent)' }}>{audienceLabel}</strong> will receive this message.
          </div>
        </div>

        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div className="sec-title" style={{ margin: '0 0 8px', fontSize: 10, color: 'var(--accent)' }}>2. Channels</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', padding: '7px 11px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7 }}>
              <input type="checkbox" id="bc-ch-wa" checked={chWa} onChange={(e) => setChWa(e.target.checked)} style={{ accentColor: '#25D366' }} /> <span style={{ color: '#25D366', fontWeight: 600 }}>WhatsApp</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', padding: '7px 11px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7 }}>
              <input type="checkbox" id="bc-ch-sms" checked={chSms} onChange={(e) => setChSms(e.target.checked)} style={{ accentColor: 'var(--accent)' }} /> <span style={{ color: 'var(--accent)', fontWeight: 600 }}>SMS</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', padding: '7px 11px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7 }}>
              <input type="checkbox" id="bc-ch-email" checked={chEmail} onChange={(e) => setChEmail(e.target.checked)} style={{ accentColor: 'var(--info)' }} /> <span style={{ color: 'var(--info)', fontWeight: 600 }}>Email</span>
            </label>
          </div>
        </div>

        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div className="sec-title" style={{ margin: 0, fontSize: 10, color: 'var(--accent)' }}>3. Message</div>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => setSaveTplOpen(true)} disabled={!body.trim()} title="Save the current message as a reusable template">💾 Save as template</button>
          </div>
          {chEmail ? (
            <div className="form-row" style={{ marginBottom: 7 }}>
              <label className="form-label" style={{ fontSize: 10 }}>Subject <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(email only)</span></label>
              <input type="text" className="form-input" id="bc-subject" placeholder="e.g. Safety notice — 22 April" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
          ) : null}
          <div className="form-row">
            <label className="form-label" style={{ fontSize: 10 }}>Message body</label>
            <textarea className="form-textarea" id="bc-body" style={{ minHeight: 140, fontFamily: 'inherit' }} placeholder="Hi {firstname}, ..." value={body} onChange={(e) => setBody(e.target.value)} />
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>Insert:</span>
              {['{firstname}', '{name}', '{role}', '{project}'].map((p) => (
                <button key={p} type="button" className="btn btn-ghost btn-xs" onClick={() => insertPlaceholder(p)} style={{ fontSize: 10 }}>{p}</button>
              ))}
              <span id="bc-body-meta" style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted)' }}>{body.length} chars</span>
            </div>
          </div>
        </div>

        {expanded ? (
          <div id="bc-recipients" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
            {audience.map((w) => (
              <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(30,42,64,.4)', fontSize: 11 }}>
                <span>{w.name}</span>
                <span style={{ color: 'var(--muted)' }}>{w.phone}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-ghost" onClick={() => setExpanded((v) => !v)} id="bc-review-btn">👥 Review recipients</button>
          <button className="btn btn-ghost" onClick={copyNumbers} title="Copy all phone numbers to your clipboard">📋 Copy numbers</button>
          <button className="btn btn-primary" id="bc-send-btn" onClick={() => send.mutate()} disabled={send.isPending || !body.trim() || (!chWa && !chSms && !chEmail)}>
            {send.isPending ? 'Sending…' : '🚀 Send broadcast'}
          </button>
        </div>
      </div>
      {saveTplOpen ? <BcSaveTemplateModal onClose={() => setSaveTplOpen(false)} messageBody={body} /> : null}
    </div>,
    document.body,
  );
}
